import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaGithub, 
  FaExternalLinkAlt, 
  FaReact, 
  FaNodeJs, 
  FaFire, 
  FaCss3Alt,
  FaPython,
  FaJs
} from 'react-icons/fa';
import { SiTailwindcss, SiMongodb, SiFirebase, SiNextdotjs } from 'react-icons/si';
import universityImage from "../images/bonnflowers.png";
import faithfeedsImage from "../images/faithfeedswebsite.png";
import { useInView } from 'react-intersection-observer';

function Projects() {
  const [activeFilter, setActiveFilter] = useState('All');
  const [showPopup, setShowPopup] = useState(false);
  const [popupProject, setPopupProject] = useState(null);
  const [sectionRef, isSectionInView] = useInView({ threshold: 0.1, triggerOnce: true });

  const projects = [
    {
      id: 1,
      title: 'Faith Feeds International',
      description: 'Faith-led initiative dedicated to restoring dignity through uplifting vulnerable communities while empowering youth to lead change through service and leadership.',
      technologies: ['Next.js', 'Tailwind CSS', 'JavaScript'],
      category: 'Web App',
      githubLink: 'https://github.com/HarryKirigwi/faith-foundation',
      liveLink: 'https://faith-foundation.vercel.app/',
      image: faithfeedsImage 
    },
    {
      id: 2,
      title: 'House of Bonn Flowers',
      description: 'Client project for a flower shop with real-time features',
      technologies: ['Nextjs', 'Postgres', 'Tailwind CSS'],
      category: 'Web App',
      githubLink: 'https://github.com/HarryKirigwi/bonn-flowers',
      liveLink: 'https://bonnflowers.vercel.app',
      image: universityImage,
      comingSoon: true
    },
    {
      id: 3,
      title: 'Lee AI Forex Trading Program',
      description: 'AI-powered forex information and trading recommendation system built with Python',
      technologies: ['Python', 'AI/ML', 'Data Analysis'],
      category: 'AI/ML',
      githubLink: 'https://github.com/HarryKirigwi/Lee-AI-Forex-Information-Trading-Recommendation-Program',
      liveLink: null,
      image: 'https://images.pexels.com/photos/164474/pexels-photo-164474.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
      comingSoon: true
    }
  ];

  const techIcons = {
    'React': <FaReact className="text-2xl text-blue-500" />,
    'Node.js': <FaNodeJs className="text-2xl text-green-500" />,
    'MongoDB': <SiMongodb className="text-2xl text-green-600" />,
    'Firebase': <SiFirebase className="text-2xl text-yellow-500" />,
    'Tailwind CSS': <SiTailwindcss className="text-2xl text-blue-400" />,
    'Framer Motion': <FaFire className="text-2xl text-purple-500" />,
    'CSS': <FaCss3Alt className="text-2xl text-blue-300" />,
    'Next.js': <SiNextdotjs className="text-2xl text-blue-600" />,
    'Python': <FaPython className="text-2xl text-blue-700" />,
    'AI/ML': <FaFire className="text-2xl text-orange-500" />,
    'Data Analysis': <FaFire className="text-2xl text-green-400" />,
    'JavaScript': <FaJs className="text-2xl text-yellow-400" />
  };

  const ProjectCard = ({ project, index }) => {
    const [ref, isInView] = useInView({ threshold: 0.2, triggerOnce: true });

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
        transition={{ duration: 1, delay: index * 0.15, ease: 'easeOut' }}
        whileHover={{ scale: 1.02, boxShadow: '0 0 20px rgba(99, 102, 241, 0.5)' }}
        className="bg-gray-800 bg-opacity-70 backdrop-blur-sm rounded-lg border border-gray-700 hover:border-indigo-500 overflow-hidden shadow-lg transition-all duration-300"
      >
        {/* Project Image */}
        <div className="relative h-48 overflow-hidden">
          <motion.img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 1, delay: index * 0.15, ease: 'easeOut' }}
          />
          <motion.div
            className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center"
            initial={{ opacity: 0 }}
            whileHover={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex space-x-6">
              <motion.a
                href={project.githubLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-indigo-500"
                whileHover={{ scale: 1.15, rotate: 5 }}
              >
                <FaGithub className="text-3xl" />
              </motion.a>
                             {project.comingSoon ? (
                 <motion.button
                   onClick={() => {
                     setPopupProject(project);
                     setShowPopup(true);
                   }}
                   className="text-white hover:text-indigo-500"
                   whileHover={{ scale: 1.15, rotate: -5 }}
                 >
                   <FaExternalLinkAlt className="text-3xl" />
                 </motion.button>
               ) : (
                <motion.a
                  href={project.liveLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-indigo-500"
                  whileHover={{ scale: 1.15, rotate: -5 }}
                >
                  <FaExternalLinkAlt className="text-3xl" />
                </motion.a>
              )}
            </div>
          </motion.div>
        </div>

        {/* Project Details */}
        <div className="p-6">
          <motion.h3
            className="text-xl font-semibold mb-3 text-white"
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
            transition={{ duration: 0.5, delay: index * 0.2 + 0.3 }}
          >
            {project.title}
          </motion.h3>
          <motion.p
            className="text-gray-400 mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: index * 0.2 + 0.4 }}
          >
            {project.description}
          </motion.p>

          {/* Technologies with Icons */}
          <div className="flex flex-wrap gap-4 mb-4">
            {project.technologies.map((tech, techIndex) => (
              <motion.div
                key={techIndex}
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 + techIndex * 0.1, ease: 'easeOut' }}
                whileHover={{ scale: 1.05 }}
                className="flex items-center space-x-2 bg-gray-700 text-gray-300 px-3 py-2 rounded-full"
              >
                {techIcons[tech]}
                <span className="text-sm">{tech}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    );
  };

  const categories = ['All', ...new Set(projects.map(project => project.category))];
  const filteredProjects = activeFilter === 'All'
    ? projects
    : projects.filter(project => project.category === activeFilter);

  return (
    <section ref={sectionRef} className="relative container mx-auto px-2 sm:px-4 py-16 overflow-hidden bg-gray-900">
      {/* Background Elements */}
      <div className="absolute top-40 left-10 w-96 h-96 bg-indigo-900 bg-opacity-20 rounded-full filter blur-3xl opacity-30"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-900 bg-opacity-20 rounded-full filter blur-3xl opacity-20"></div>

      <div className="relative z-10">
        {/* Section Title */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={isSectionInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ duration: 1, ease: 'easeOut' }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-500">
            My Projects
          </h2>
        </motion.div>

        {/* Category Filters */}
        <div className="flex justify-center mb-12 space-x-4 flex-wrap gap-4">
          {categories.map((category, index) => (
            <motion.button
              key={category}
              initial={{ opacity: 0, y: -20 }}
              animate={isSectionInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
              transition={{ duration: 0.5, delay: index * 0.15, ease: 'easeOut' }}
              whileHover={{ scale: 1.05, boxShadow: '0 0 15px rgba(99, 102, 241, 0.7)' }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveFilter(category)}
              className={`
                px-4 py-2 rounded-full transition-all duration-300
                bg-gray-800 bg-opacity-60 border border-gray-700
                ${activeFilter === category 
                  ? 'border-indigo-500 text-indigo-300 bg-indigo-900 bg-opacity-20' 
                  : 'text-gray-400 hover:border-indigo-500 hover:text-indigo-300'
                }
              `}
            >
              {category}
            </motion.button>
          ))}
        </div>

        {/* Projects Grid */}
        <AnimatePresence>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project, index) => (
              <ProjectCard key={project.id} project={project} index={index} />
            ))}
          </div>
        </AnimatePresence>

        {/* Coming Soon Popup */}
        <AnimatePresence>
          {showPopup && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                             onClick={() => {
                 setShowPopup(false);
                 setPopupProject(null);
               }}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="bg-gray-800 rounded-lg p-8 max-w-md w-full border border-gray-700"
                onClick={(e) => e.stopPropagation()}
              >
                                 <div className="text-center">
                   <div className="text-6xl mb-4">🚀</div>
                   <h3 className="text-2xl font-bold text-white mb-4">Coming Soon!</h3>
                   <p className="text-gray-300 mb-6">
                     The live version of <span className="text-indigo-400 font-semibold">{popupProject?.title}</span> is currently unavailable. 
                     You can check out the source code on GitHub in the meantime!
                   </p>
                   <div className="flex space-x-4 justify-center">
                     <motion.a
                       href={popupProject?.githubLink}
                       target="_blank"
                       rel="noopener noreferrer"
                       className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg transition-colors duration-300"
                       whileHover={{ scale: 1.05 }}
                       whileTap={{ scale: 0.95 }}
                     >
                       View on GitHub
                     </motion.a>
                    <motion.button
                      onClick={() => {
                 setShowPopup(false);
                 setPopupProject(null);
               }}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg transition-colors duration-300"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Close
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>                                       
    </section>
  );
}

export default Projects;